<template>
  <div class="w-100">
    <b-row no-gutters class="justify-content-center">
      <!-- Desktop: 컬럼 형식 (xl 이상) -->
      <b-col cols="12" class="d-none d-xl-flex justify-content-between">
        <div
          v-for="(lv1_info, lv1_idx) in sb_toc"
          :key="'dropdown_lv1' + lv1_idx"
          class="flex-grow-1 flex-column navbar-submenu justify-content-start px-3 uniform-width border-left"
          :class="{ 'border-right': lv1_idx === sb_toc.length - 1 }"
        >
          <div class="title-container pb-3">
            <span class="footer-title fw-700 text-white" :style="footerTitleStyle">
              {{ getLocalizedName(lv1_info, lang) }}
            </span>
          </div>

          <div v-for="(lv2_info, lv2_idx) in lv1_info.children" :key="'dropdown_lv2' + lv2_idx">
            <div
              v-if="
                lv2_info.appendix_type === null || report_types.includes(lv2_info.appendix_type)
              "
              class="link-container py-2"
              @click="update_lv2_info(lv1_info, lv2_info)"
            >
              <b-link class="py-2 no-underline">
                <span class="text-white footer-link f-90 fw-600" :style="footerTitleStyle">
                  {{ getLocalizedName(lv2_info, lang) }}
                </span>
              </b-link>
            </div>
          </div>
        </div>
      </b-col>

      <!-- Mobile/Tablet: 아코디언 (xl 미만) -->
      <b-col cols="12" class="d-flex d-xl-none">
        <div class="accordion w-100 mobile-footer-nav" role="tablist">
          <div
            v-for="(lv1_info, lv1_idx) in sb_toc"
            :key="'footer_mobile_lv1_' + lv1_idx"
            class="footer-nav-section"
          >
            <b-button
              v-b-toggle="'footer-nav-accordion-' + lv1_idx"
              block
              variant="none"
              class="mobile-footer-lv1 d-flex justify-content-between align-items-center"
            >
              <span>
                {{ getLocalizedName(lv1_info, lang) }}
              </span>
              <svg
                class="mobile-footer-chevron"
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708"
                />
              </svg>
            </b-button>
            <b-collapse
              :id="'footer-nav-accordion-' + lv1_idx"
              role="tabpanel"
              accordion="footer-nav-accordion"
            >
              <div class="d-flex flex-column pb-2">
                <b-link
                  v-for="(lv2_info, lv2_idx) in lv1_info.children"
                  v-show="
                    lv2_info.appendix_type === null || report_types.includes(lv2_info.appendix_type)
                  "
                  :key="'footer_mobile_lv2_' + lv1_idx + '_' + lv2_idx"
                  class="mobile-footer-lv2"
                  @click="update_lv2_info(lv1_info, lv2_info)"
                >
                  {{ getLocalizedName(lv2_info, lang) }}
                </b-link>
              </div>
            </b-collapse>
          </div>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapState, mapMutations, mapGetters, mapActions } from 'vuex';
import { updateParamsFromQueryString } from '@/components/util.js';
import { getLocalizedName } from '@/utils/i18n';

export default {
  name: 'FooterNav',
  props: {
    lang: { type: String, default: 'KOR' }
  },
  computed: {
    ...mapState('report_list', ['view_mode']),
    ...mapGetters('assurance', ['latest_assurance']),
    ...mapGetters('story_board', [
      'selected_lv1',
      'selected_lv2',
      'sb_toc',
      'selected_viewer_disclosures'
    ]),
    ...mapGetters('report_list', ['selected_report']),
    ...mapGetters(['is_viewer']),
    report_types() {
      const user_selected_report_type = Object.keys(this.selected_report.report_type).map(
        item => item.split('v')[0]
      );
      if (this.latest_assurance) {
        user_selected_report_type.push('ASSURANCE');
      }

      if (this.selected_report?.data_center) {
        user_selected_report_type.push('DATACENTER');
      }
      return user_selected_report_type;
    },
    // lv1_info 개수에 따른 폰트 크기 조절 (5개 이하: 기본, 6개 이상: 1개 증가마다 6% 감소)
    fontSizeScale() {
      const count = this.sb_toc.length;
      return count <= 5 ? 1 : 1 - 0.08 * (count - 5);
    },
    footerTitleStyle() {
      return {
        fontSize: 100 * this.fontSizeScale + '%'
      };
    }
  },
  methods: {
    ...mapMutations('story_board', ['update_selected_lv1', 'update_selected_lv2']),
    ...mapActions('story_board', ['fetch_viewer_sb_disclosures']),
    getLocalizedName,
    update_lv2_info(lv1_info, lv2_info) {
      window.scrollTo(0, 0);
      this.update_selected_lv1({
        lv1_info: lv1_info,
        view_mode: this.is_viewer ? 'viewer' : 'editor'
      });
      this.update_selected_lv2({
        lv2_info: lv2_info,
        view_mode: this.is_viewer ? 'viewer' : 'editor'
      });

      updateParamsFromQueryString('lv1', lv1_info.name, true);
      updateParamsFromQueryString('lv2', lv2_info.name, true);

      if (!this.selected_viewer_disclosures) {
        this.fetch_viewer_sb_disclosures({
          report_code: this.current_report_code,
          selected_lv_info: {
            lv1_info: lv1_info,
            lv2_info: lv2_info
          }
        });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.footer-title,
.footer-link {
  white-space: pre-line;
}
.uniform-width {
  flex-basis: 0;
  flex-grow: 1;
}

.title-container {
  line-height: 1.2;
}
.link-container {
  line-height: 1.2;
  .no-underline {
    text-decoration: none !important;
  }

  .footer-link {
    opacity: 0.5;

    &:hover {
      opacity: 1;
    }
  }
}

.mobile-footer-nav {
  .footer-nav-section {
    border-top: 1px solid rgba(255, 255, 255, 0.15);

    &:last-child {
      border-bottom: 1px solid rgba(255, 255, 255, 0.15);
    }
  }

  .mobile-footer-lv1 {
    color: #fff;
    font-weight: 700;
    font-size: 1rem;
    padding: 0.85rem 0.25rem;
    text-align: left;
    box-shadow: none;
    white-space: normal;
    line-height: 1.3;

    &:hover,
    &:focus,
    &:active {
      color: #fff;
      background-color: transparent;
      box-shadow: none;
    }

    .mobile-footer-chevron {
      transition: transform 0.25s ease;
      flex-shrink: 0;
      margin-left: 0.5rem;
    }

    &.not-collapsed .mobile-footer-chevron {
      transform: rotate(180deg);
    }
  }

  .mobile-footer-lv2 {
    color: #fff;
    opacity: 0.6;
    font-weight: 600;
    font-size: 0.9rem;
    padding: 0.55rem 0.5rem;
    text-decoration: none !important;
    line-height: 1.35;

    &:hover,
    &:active {
      opacity: 1;
      color: #fff;
    }
  }
}
</style>
